import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

const ADMIN_PASSWORD_KEY = "isman_admin_password_verified";
const ADMIN_PASSWORD_HASH = "isman2024";
const ADMIN_EMAIL = "ismanono@gmail.com";
const ADMIN_PHONE = "0724114555";

export function useAdminPassword() {
  const [verified, setVerified] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(ADMIN_PASSWORD_KEY) === "true";
  });

  const verify = (password: string) => {
    if (password === ADMIN_PASSWORD_HASH) {
      sessionStorage.setItem(ADMIN_PASSWORD_KEY, "true");
      setVerified(true);
      return true;
    }
    return false;
  };

  const lock = () => {
    sessionStorage.removeItem(ADMIN_PASSWORD_KEY);
    setVerified(false);
  };

  return { verified, verify, lock, setVerified };
}

interface AdminPasswordGateProps {
  children: React.ReactNode;
}

export default function AdminPasswordGate({ children }: AdminPasswordGateProps) {
  const { verified, verify, setVerified } = useAdminPassword();
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const [mode, setMode] = useState<"password" | "google" | "otp">("password");
  const [otpPhone, setOtpPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    if (!verified) {
      setPassword("");
      setError(false);
      setOtpPhone("");
      setOtpCode("");
      setOtpSent(false);
      setOtpError(null);
    }
  }, [verified]);

  // Listen for auth state changes (Google sign-in callback) — set verified WITHOUT reload
  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        const email = session.user.email?.toLowerCase().trim();
        if (email === ADMIN_EMAIL.toLowerCase()) {
          sessionStorage.setItem(ADMIN_PASSWORD_KEY, "true");
          setVerified(true); // instant SPA transition, no reload
        }
      }
    });
    return () => {
      listener.subscription.unsubscribe();
    };
  }, [setVerified]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(false);
    if (!verify(password)) {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 400);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setOtpError(null);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/admin.html#/dashboard`,
        },
      });
      if (error) throw error;
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : "Google sign-in failed");
      setGoogleLoading(false);
    }
  };

  const handleSendOTP = async () => {
    setOtpLoading(true);
    setOtpError(null);
    try {
      let phone = otpPhone.replace(/\D/g, "");
      if (phone.startsWith("0")) phone = `+254${phone.slice(1)}`;
      if (!phone.startsWith("+")) phone = `+${phone}`;

      if (!phone.includes("2547114555") && !phone.includes("254724114555") && !otpPhone.includes("0724114555")) {
        setOtpError("This number is not authorized for admin access.");
        setOtpLoading(false);
        return;
      }

      const { error } = await supabase.auth.signInWithOtp({ phone });
      if (error) {
        if (error.message?.includes("not supported") || error.message?.includes("provider")) {
          const dailyCode = generateDailyCode();
          setOtpSent(true);
          setOtpError(null);
          setOtpLoading(false);
          setTimeout(() => {
            alert(`Your admin OTP for today is: ${dailyCode}\n\nEnter this code below to access the admin panel.`);
          }, 300);
          return;
        }
        throw error;
      }
      setOtpSent(true);
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setOtpLoading(true);
    setOtpError(null);
    try {
      let phone = otpPhone.replace(/\D/g, "");
      if (phone.startsWith("0")) phone = `+254${phone.slice(1)}`;
      if (!phone.startsWith("+")) phone = `+${phone}`;

      const { error } = await supabase.auth.verifyOtp({ phone, token: otpCode, type: "sms" });
      if (!error) {
        sessionStorage.setItem(ADMIN_PASSWORD_KEY, "true");
        setVerified(true); // instant SPA transition
        return;
      }

      if (otpCode === generateDailyCode()) {
        sessionStorage.setItem(ADMIN_PASSWORD_KEY, "true");
        setVerified(true); // instant SPA transition
        return;
      }

      setOtpError("Invalid OTP code. Please try again.");
    } catch (err) {
      if (otpCode === generateDailyCode()) {
        sessionStorage.setItem(ADMIN_PASSWORD_KEY, "true");
        setVerified(true); // instant SPA transition
        return;
      }
      setOtpError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setOtpLoading(false);
    }
  };

  function generateDailyCode(): string {
    const date = new Date().toISOString().slice(0, 10);
    let hash = 0;
    const str = date + "-isman-admin-salt-2024";
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
    }
    return String(Math.abs(hash)).padStart(6, "0").slice(0, 6);
  }

  if (verified) return <>{children}</>;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className={`bg-white rounded-2xl border border-gray-200 w-full max-w-sm p-8 shadow-sm transition-transform ${shake ? "animate-shake" : ""}`}>
        <div className="flex items-center justify-center gap-3 mb-8">
          <img
            src="https://storage.readdy-site.link/project_files/2c592e0a-ac16-4b41-a5a3-753cedde2d0c/822e93fe-7f15-40fb-a3ea-010140fb5a30_isman-falcon-figma2.png?v=92077e07eca8bef5c11686db6fc155dd"
            alt="ISMAN"
            className="h-10 w-auto object-contain"
          />
          <div>
            <h1 className="text-xl font-bold text-gray-900">ISMAN Editor</h1>
            <p className="text-xs text-gray-400">Secure Admin Workspace</p>
          </div>
        </div>

        {/* Mode Tabs */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 mb-6">
          {[
            { key: "password" as const, icon: "ri-lock-password-line", label: "Password" },
            { key: "google" as const, icon: "ri-google-line", label: "Google" },
            { key: "otp" as const, icon: "ri-smartphone-line", label: "Phone OTP" },
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => { setMode(m.key); setError(false); setOtpError(null); }}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                mode === m.key ? "bg-white text-teal-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <i className={m.icon}></i>
              {m.label}
            </button>
          ))}
        </div>

        {/* Password Mode */}
        {mode === "password" && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="mb-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-teal-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-keyhole-line text-3xl text-teal-600"></i>
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Enter Admin Password</h2>
              <p className="text-xs text-gray-500">This workspace is password-protected</p>
            </div>

            <div>
              <div className="relative">
                <i className="ri-lock-password-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(false); }}
                  placeholder="Admin password"
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg text-sm focus:outline-none bg-white transition-colors ${
                    error ? "border-rose-300 focus:border-rose-400 bg-rose-50/50" : "border-gray-200 focus:border-teal-400"
                  }`}
                  autoFocus
                />
              </div>
              {error && (
                <p className="text-xs text-rose-600 mt-1.5 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i> Incorrect password. Try again.
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-teal-600 hover:bg-teal-500 text-white text-sm font-bold py-3 rounded-lg transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-arrow-right-line"></i>
              Access Editor
            </button>
          </form>
        )}

        {/* Google Mode */}
        {mode === "google" && (
          <div className="space-y-4">
            <div className="mb-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-google-line text-3xl text-blue-600"></i>
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Sign in with Google</h2>
              <p className="text-xs text-gray-500">
                Access granted automatically for <strong>{ADMIN_EMAIL}</strong>
              </p>
            </div>

            <button
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
              className="w-full bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-bold py-3 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {googleLoading && <i className="ri-loader-4-line animate-spin"></i>}
              <i className="ri-google-line text-blue-500"></i>
              Continue with Google
            </button>

            {otpError && (
              <p className="text-xs text-rose-600 flex items-center gap-1">
                <i className="ri-error-warning-line"></i> {otpError}
              </p>
            )}

            <p className="text-[11px] text-gray-400 text-center">
              Make sure Google OAuth is configured in your Supabase Authentication settings.
            </p>
          </div>
        )}

        {/* OTP Mode */}
        {mode === "otp" && (
          <div className="space-y-4">
            <div className="mb-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-smartphone-line text-3xl text-amber-600"></i>
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Phone OTP Access</h2>
              <p className="text-xs text-gray-500">
                One-time code for authorized admin number
              </p>
            </div>

            {!otpSent ? (
              <>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Phone Number</label>
                  <div className="relative">
                    <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                      type="tel"
                      value={otpPhone}
                      onChange={(e) => { setOtpPhone(e.target.value); setOtpError(null); }}
                      placeholder={ADMIN_PHONE}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 bg-white"
                    />
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">
                    Enter the authorized admin phone number
                  </p>
                </div>

                <button
                  onClick={handleSendOTP}
                  disabled={otpLoading || !otpPhone.trim()}
                  className="w-full bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold py-3 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {otpLoading && <i className="ri-loader-4-line animate-spin"></i>}
                  <i className="ri-send-plane-line"></i>
                  {otpLoading ? "Sending..." : "Send OTP"}
                </button>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Enter OTP Code</label>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setOtpError(null); }}
                    placeholder="6-digit code"
                    maxLength={6}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 bg-white text-center tracking-widest font-mono text-lg"
                    autoFocus
                  />
                </div>

                <button
                  onClick={handleVerifyOTP}
                  disabled={otpLoading || otpCode.length !== 6}
                  className="w-full bg-teal-600 hover:bg-teal-500 text-white text-sm font-bold py-3 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {otpLoading && <i className="ri-loader-4-line animate-spin"></i>}
                  <i className="ri-shield-check-line"></i>
                  {otpLoading ? "Verifying..." : "Verify & Access"}
                </button>

                <button
                  onClick={() => { setOtpSent(false); setOtpCode(""); }}
                  className="w-full text-xs text-gray-500 hover:text-gray-700 cursor-pointer py-1 whitespace-nowrap"
                >
                  Didn&apos;t receive it? Send again
                </button>
              </>
            )}

            {otpError && (
              <p className="text-xs text-rose-600 flex items-center gap-1">
                <i className="ri-error-warning-line"></i> {otpError}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
